// ---------------------------------------------------------------------------
// Recruiter API client
// ---------------------------------------------------------------------------
// Conversation CRUD + SSE streaming for the AI recruiter agent.
// Follows the same pattern as shortlists.ts (handle401, jsonFetch, API_BASE).

const API_BASE =
  typeof window === "undefined"
    ? process.env.ALISTER_API_URL ?? "http://127.0.0.1:8000"
    : "";

// -- Response types ----------------------------------------------------------

export interface ConversationSummary {
  id: number;
  title: string | null;
  status: string;
  candidate_count: number;
  created_at: string;
  updated_at: string | null;
}

export interface MessageItem {
  id: number;
  role: "user" | "assistant" | "tool_call" | "tool_result";
  content: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface AgentContext {
  role_type?: string | null;
  seniority?: string | null;
  tech_stack?: string[];
  location_preference?: string | null;
  must_haves?: string[];
  nice_to_haves?: string[];
  company_context?: string | null;
  additional_notes?: string | null;
  gathering_complete?: boolean;
}

export interface ConversationDetail {
  id: number;
  title: string | null;
  status: string;
  shortlist_id: number | null;
  agent_context: AgentContext | null;
  messages: MessageItem[];
  created_at: string;
  updated_at: string | null;
}

export interface CreateConversationResult {
  id: number;
  shortlist_id: number;
  title: string | null;
  status: string;
  created_at: string;
}

// -- Helpers -----------------------------------------------------------------

function handle401(): never {
  if (typeof window !== "undefined") {
    window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
  }
  // Return a never-resolving promise so callers don't continue
  return new Promise<never>(() => {}) as never;
}

async function jsonFetch<T>(
  url: string,
  init?: RequestInit
): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
  });

  if (response.status === 401) {
    return handle401();
  }

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json();
}

// -- Linked-entity errors ----------------------------------------------------

export class LinkedShortlistError extends Error {
  constructor(
    public linkedShortlistId: number,
    public linkedShortlistName: string | null,
  ) {
    super("Conversation has a linked shortlist");
  }
}

// -- Conversation CRUD -------------------------------------------------------

export async function createConversation(): Promise<CreateConversationResult> {
  return jsonFetch<CreateConversationResult>(
    `${API_BASE}/api/v1/recruiter/conversations`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
}

export async function listConversations(): Promise<ConversationSummary[]> {
  const res = await jsonFetch<{ conversations: ConversationSummary[] }>(
    `${API_BASE}/api/v1/recruiter/conversations`
  );
  return res.conversations;
}

export async function getConversation(
  id: number
): Promise<ConversationDetail> {
  return jsonFetch<ConversationDetail>(
    `${API_BASE}/api/v1/recruiter/conversations/${id}`
  );
}

export async function deleteConversation(
  id: number,
  alsoDeleteShortlist = false,
  keepShortlist = false,
): Promise<void> {
  const params = new URLSearchParams();
  if (alsoDeleteShortlist) params.set("also_delete_shortlist", "true");
  if (keepShortlist) params.set("keep_shortlist", "true");
  const qs = params.toString();
  const response = await fetch(
    `${API_BASE}/api/v1/recruiter/conversations/${id}${qs ? `?${qs}` : ""}`,
    { method: "DELETE", credentials: "include" },
  );

  if (response.status === 401) return handle401();

  if (response.status === 409) {
    const body = await response.json();
    if (body?.detail?.code === "linked_shortlist") {
      throw new LinkedShortlistError(
        body.detail.linked_shortlist_id,
        body.detail.linked_shortlist_name,
      );
    }
    throw new Error("Conflict");
  }

  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
}

export async function getConversationContext(
  id: number
): Promise<AgentContext | null> {
  const res = await jsonFetch<{ context: AgentContext | null }>(
    `${API_BASE}/api/v1/recruiter/conversations/${id}/context`
  );
  return res.context;
}

// -- SSE streaming -----------------------------------------------------------

/**
 * Stream a message to the recruiter agent via SSE.
 * POSTs to the SSE endpoint and reads the event stream, calling onEvent
 * for each parsed SSE event.
 */
export async function streamMessage(
  conversationId: number,
  content: string,
  onEvent: (event: string, data: Record<string, unknown>) => void,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(
    `${API_BASE}/api/v1/recruiter/conversations/${conversationId}/messages`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
      signal,
    }
  );

  if (response.status === 401) {
    handle401();
    return;
  }

  if (!response.ok) {
    throw new Error(`Stream request failed: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Response body is not readable");
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "message";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines from the buffer
      const lines = buffer.split("\n");
      // Keep the last (possibly incomplete) line in the buffer
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmed = line.trim();

        if (trimmed === "") {
          // Empty line = end of event, reset event type
          currentEvent = "message";
          continue;
        }

        if (trimmed.startsWith("event:")) {
          currentEvent = trimmed.slice(6).trim();
          continue;
        }

        if (trimmed.startsWith("data:")) {
          const dataStr = trimmed.slice(5).trim();
          if (!dataStr) continue;

          try {
            const data = JSON.parse(dataStr) as Record<string, unknown>;
            onEvent(currentEvent, data);
          } catch {
            // Skip malformed JSON data lines
          }
        }
      }
    }

    // Process any remaining buffer content
    if (buffer.trim()) {
      const trimmed = buffer.trim();
      if (trimmed.startsWith("data:")) {
        const dataStr = trimmed.slice(5).trim();
        if (dataStr) {
          try {
            const data = JSON.parse(dataStr) as Record<string, unknown>;
            onEvent(currentEvent, data);
          } catch {
            // Skip malformed JSON
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
